import {
  type CloudFlowNodeType,
  type Member,
  ModelType,
  type NodeTransformationAverage,
  type NodeTransformationConcatenation,
  type NodeTransformationExtract,
  type NodeTransformationFirstItem,
  type NodeTransformationJoin,
  type NodeTransformationLastItem,
  type NodeTransformationLowerCase,
  NodeTransformationType,
  type NodeTransformationUpperCase,
  type StructureApiServiceModelDescriptor,
  type UnwrappedApiServiceModelDescriptor,
} from "@doitintl/cmp-models";

import {
  getModelByPath,
  getNodeOutputModel,
  getRelativePath,
  ModelNotFoundException,
  type OutputModelFetcher,
  wrapModelWithListModel,
} from "../model-descriptors";
import { type NodeModelWithId } from "../types";

export async function calculateTransformationNodeOutputModel(
  transformationNode: NodeModelWithId<CloudFlowNodeType.TRANSFORMATION>,
  nodes: NodeModelWithId[],
  outputModelFetcher: OutputModelFetcher
) {
  const transformationNodeOutputModel = await getNodeOutputModel(
    outputModelFetcher,
    nodes,
    transformationNode.parameters.referencedNodeId
  );
  if (transformationNodeOutputModel === null) {
    return null;
  }

  let outputModel: UnwrappedApiServiceModelDescriptor = JSON.parse(
    JSON.stringify(getModelByPath(transformationNodeOutputModel, transformationNode.parameters.referencedField))
  );
  for (const transformation of transformationNode.parameters.transformations) {
    switch (transformation.type) {
      case NodeTransformationType.EXTRACT:
      case NodeTransformationType.CONCATENATION:
      case NodeTransformationType.UPPER_CASE:
      case NodeTransformationType.LOWER_CASE: {
        outputModel = extendStructureModel(outputModel, transformation, ModelType.STRING);
        continue;
      }
      case NodeTransformationType.AVERAGE: {
        outputModel = extendStructureModel(outputModel, transformation, ModelType.FLOAT);
        continue;
      }

      case NodeTransformationType.FIRST_ITEM:
      case NodeTransformationType.LAST_ITEM: {
        outputModel = collectionItemModelExtension(
          outputModel,
          transformation,
          transformationNode.parameters.referencedField
        );
        continue;
      }
      case NodeTransformationType.JOIN: {
        outputModel = await extendReferenceModel(outputModelFetcher, nodes, outputModel, transformation);
        continue;
      }

      default:
        throw new Error(`Transformation ${JSON.stringify(transformation)} is not implemented yet!`);
    }
  }

  return wrapModelWithListModel(outputModel);
}

function extendStructureModel(
  transformationNodeOutputModel: UnwrappedApiServiceModelDescriptor,
  transformation:
    | NodeTransformationConcatenation
    | NodeTransformationExtract
    | NodeTransformationUpperCase
    | NodeTransformationLowerCase
    | NodeTransformationAverage,
  type: ModelType.STRING | ModelType.FLOAT | ModelType.INTEGER
) {
  const outputModel = extractStructureModel(transformationNodeOutputModel, transformation.type);

  outputModel.members[transformation.newFieldName] = { model: { type } };
  return outputModel;
}

function collectionItemModelExtension(
  transformationNodeOutputModel: UnwrappedApiServiceModelDescriptor,
  transformation: NodeTransformationFirstItem | NodeTransformationLastItem,
  dataSourceReferencedField: string[]
) {
  const outputModel = extractStructureModel(transformationNodeOutputModel, transformation.type);
  const relativePath = getRelativePath(dataSourceReferencedField, transformation.payload.referencedField);
  const referencedCollectionModel = getModelByPath(transformationNodeOutputModel, relativePath);
  if (referencedCollectionModel.type !== ModelType.LIST) {
    throw new ModelNotFoundException(`${transformation.type} transformation node can pick items only from lists`);
  }

  outputModel.members[transformation.newFieldName] = referencedCollectionModel.member;

  return outputModel;
}

async function extendReferenceModel(
  outputModelFetcher: OutputModelFetcher,
  nodes: NodeModelWithId[],
  transformationNodeOutputModel: UnwrappedApiServiceModelDescriptor,
  transformation: NodeTransformationJoin
) {
  const outputModel = extractStructureModel(transformationNodeOutputModel, transformation.type);

  const referencedNodeOutputModel = await getNodeOutputModel(
    outputModelFetcher,
    nodes,
    transformation.payload.referencedNodeId
  );

  if (referencedNodeOutputModel === null) {
    throw new ModelNotFoundException(
      `${transformation.type} transformation node cannot reference a node without output model`
    );
  }

  const referencedModel = getModelByPath(referencedNodeOutputModel, transformation.payload.referencedField);

  outputModel.members[transformation.newFieldName] = { model: referencedModel };

  return outputModel;
}

function extractStructureModel(
  transformationNodeOutputModel: UnwrappedApiServiceModelDescriptor,
  transformationType: NodeTransformationType
): StructureApiServiceModelDescriptor<Member> {
  let outputModel = transformationNodeOutputModel;
  if (outputModel.type === ModelType.LIST) {
    outputModel = outputModel.member.model;
  }

  if (outputModel.type !== ModelType.STRUCTURE) {
    throw new ModelNotFoundException(
      `${transformationType} transformation node can only reference structures or list of structures`
    );
  }
  return outputModel;
}
